"use client";

import { useMemo } from "react";
import { cn } from "@ui/lib";
import {
  calculateEffort,
  calculateManualBenchmark,
  type ModelId,
} from "../lib/statistics";

const FREQ_COLORS: Record<string, string> = {
  daily: "text-emerald-500",
  three: "text-amber-500",
  weekly: "text-rose-500",
};

interface ManualEffortProps {
  prompts: number;
  selectedModels: Set<ModelId>;
  p: number;
  conf: number;
}

export function ManualEffort({
  prompts,
  selectedModels,
  p,
  conf,
}: ManualEffortProps) {
  const numModels = selectedModels.size;

  const effortData = useMemo(
    () => calculateEffort(prompts, numModels, p, conf),
    [prompts, numModels, p, conf],
  );

  const benchmark = useMemo(
    () => calculateManualBenchmark(prompts, numModels, p, conf),
    [prompts, numModels, p, conf],
  );

  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="text-lg font-semibold">The Hidden Cost of Manual Tracking</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Tracking AI visibility by hand means opening each model, entering each
        prompt, reading the response, recording whether the brand appears, and
        doing it again for every model you care about. We estimate{" "}
        <strong className="text-foreground">~2.5 minutes per prompt per model</strong>{" "}
        (opening the tool, typing the prompt, waiting for the response, recording
        the result). That&apos;s a conservative estimate.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {effortData.map((d) => (
          <div
            key={d.key}
            className="rounded-xl border bg-muted/50 p-4 space-y-2"
          >
            <h4 className="text-sm font-semibold">{d.label}</h4>
            <Row
              label="Runs per model / month"
              value={d.totalRunsPerModel.toLocaleString()}
              className={FREQ_COLORS[d.key]}
            />
            <Row label="Models tracked" value={String(numModels)} />
            <Row
              label="Total prompt runs / month"
              value={d.totalRuns.toLocaleString()}
              className={FREQ_COLORS[d.key]}
            />
            <Row label="Time per run (manual est.)" value="~2.5 min" />
            <Row
              label="Manual hours / month"
              value={`${d.hours} hrs`}
              className="text-rose-500"
            />
            <Row
              label="≈ Hours per week"
              value={`${d.hoursPerWeek} hrs/wk`}
              className="text-rose-500"
            />
            <Row
              label="Statistical accuracy"
              value={`±${d.me}%`}
              className={FREQ_COLORS[d.key]}
            />
          </div>
        ))}
      </div>

      {/* Benchmark Banner */}
      <div className="mt-6 rounded-xl border border-rose-500/25 bg-rose-500/5 p-5 text-sm text-muted-foreground leading-relaxed">
        <strong className="text-foreground">
          What would it take to hit ±10% accuracy manually?
        </strong>
        <br />
        At {(conf * 100).toFixed(0)}% confidence with a {(p * 100).toFixed(0)}%
        observed mention rate, you&apos;d need{" "}
        <strong className="text-foreground">
          {benchmark.nNeeded} runs per prompt
        </strong>{" "}
        to stay within a ±10% margin of error. Across{" "}
        <strong className="text-foreground">
          {numModels} model{numModels !== 1 ? "s" : ""}
        </strong>{" "}
        and{" "}
        <strong className="text-foreground">{prompts} prompts</strong>, that&apos;s{" "}
        <strong className="text-foreground">
          {benchmark.totalManualRuns.toLocaleString()} total prompt runs
        </strong>{" "}
        — every month.
        <span className="mt-2 block text-2xl font-bold text-rose-500">
          {benchmark.totalManualHours} hrs/month ·{" "}
          {benchmark.totalManualHoursPerWeek} hrs/week
        </span>
        of manual work, just to collect the data — before any analysis,
        reporting, or action. That&apos;s not a workflow; that&apos;s a full-time job.
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-border/50 py-1.5 text-xs last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-medium", className)}>{value}</span>
    </div>
  );
}
